import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { Button } from '../../components/ui/Button';
import { DebtTermHint } from '../../components/liabilities/DebtTermHint';
import {
  useLiabilityQuery,
  useSimulateExtraMutation,
} from '../../features/liabilities/queries';
import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { effectiveMonthlyPayment } from '../../utils/debtPlanUtils';
import { formatFreeEta } from '../../lib/debtDisplayUtils';
import { cn } from '../../utils/cn';

const PAGE_PAD = 'px-4 pb-8 pt-2 md:px-0';
const DEBOUNCE_MS = 350;

export function DividasSimulator() {
  const { id } = useParams();
  const { data: liability, isPending, isFetching } = useLiabilityQuery(id);
  const liabilityShell = useQueryShellState({ data: liability, isPending, isFetching });
  const simulate = useSimulateExtraMutation();
  const [extra, setExtra] = useState(40);

  const basePayment = liability ? effectiveMonthlyPayment(liability) : 0;
  const sliderMax = Math.max(100, Math.round(basePayment * 2) || 100);

  useEffect(() => {
    if (!id || !liability || basePayment <= 0) return undefined;
    if (extra <= 0) {
      simulate.reset?.();
      return undefined;
    }
    const handle = setTimeout(() => {
      simulate.mutate({
        id,
        payload: {
          extra_amount: String(extra),
          mode: 'monthly',
        },
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on extra/id
  }, [extra, id, basePayment, liability]);

  const result = simulate.data;
  const monthsSaved = Number(result?.months_saved) || 0;
  const interestSaved = Number(result?.interest_saved_eur) || 0;
  const accelMonths = Number(result?.accelerated_payoff_months);
  const freeDate =
    !Number.isNaN(accelMonths) && accelMonths > 0 ? formatFreeEta(accelMonths) : '';

  const rewardLine = useMemo(() => {
    if (!result || result.requires_more_data || !freeDate) return null;
    const newPay = basePayment + extra;
    return pt.liabilities.simulator.rewardLine
      .replace('{newPayment}', `€${Math.round(newPay)}`)
      .replace('{basePayment}', `€${Math.round(basePayment)}`)
      .replace('{date}', freeDate);
  }, [result, freeDate, basePayment, extra]);

  if (liabilityShell.showBlockingSkeleton) {
    return (
      <div className={cn('mx-auto flex max-w-lg flex-col gap-6', PAGE_PAD)}>
        <SectionSkeleton variant="card-grid" />
      </div>
    );
  }

  if (!liability) return null;

  return (
    <div className={cn('mx-auto flex max-w-lg flex-col gap-6 overflow-x-hidden', PAGE_PAD)}>
      <header>
        <Button asChild variant="ghost" className="mb-2 -ml-2 hidden md:inline-flex">
          <Link to={paths.dividas}>{pt.common.back}</Link>
        </Button>
        <h1 className="font-display text-2xl font-semibold text-foreground">
          {pt.liabilities.simulator.title}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{liability.name}</p>
      </header>

      {basePayment <= 0 ? (
        <p className="text-sm text-amber-800 dark:text-amber-300">
          {pt.liabilities.simulator.requiresData}
        </p>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="text-center text-sm font-medium text-muted-foreground">
              {pt.liabilities.simulator.question}
            </p>
            <p className="mt-3 text-center font-display text-3xl font-bold tabular-nums text-sage-700 dark:text-sage-300">
              +€{extra}
              <span className="text-base font-medium text-muted-foreground">
                {' '}
                {pt.liabilities.simulator.perMonth}
              </span>
            </p>
            <label className="mt-4 block">
              <span className="sr-only">{pt.liabilities.simulator.extraLabel}</span>
              <input
                type="range"
                min={0}
                max={sliderMax}
                step={5}
                value={extra}
                onChange={(e) => setExtra(Number(e.target.value))}
                className="w-full accent-sage-600"
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>€0</span>
                <span>+€{sliderMax}</span>
              </div>
            </label>
          </div>

          {extra > 0 && result?.requires_more_data ? (
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {pt.liabilities.simulator.requiresData}
            </p>
          ) : null}

          {extra > 0 && result && !result.requires_more_data ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-sage-200 bg-sage-50 p-4 text-center dark:border-sage-800 dark:bg-sage-900/30">
                  <p className="font-display text-xl font-bold tabular-nums text-sage-800 dark:text-sage-200">
                    {pt.liabilities.simulator.monthsSavedValue.replace(
                      '{n}',
                      String(monthsSaved),
                    )}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {pt.liabilities.simulator.monthsSaved}
                  </p>
                </div>
                <div className="rounded-2xl border border-sage-200 bg-sage-50 p-4 text-center dark:border-sage-800 dark:bg-sage-900/30">
                  <p className="font-display text-xl font-bold tabular-nums text-sage-800 dark:text-sage-200">
                    <MoneyDisplay value={interestSaved} size="sm" />
                  </p>
                  <p className="mt-1 inline-flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    {pt.liabilities.simulator.interestSaved}
                    <DebtTermHint term="interest" variant="icon" />
                  </p>
                </div>
              </div>
              {rewardLine ? (
                <p className="rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm leading-relaxed text-muted-foreground">
                  {rewardLine}
                </p>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default DividasSimulator;
